'use client';

import { cn } from '@lib/utils';
import { useCallback, useRef, useState } from 'react';
import { useTooltip } from './tooltip/useTooltip';

interface TextWithTooltipProps {
  children: React.ReactNode;
  className?: string;
  tooltipContent?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

export function TextWithTooltip({
  children,
  className,
  tooltipContent,
  side = 'bottom',
}: TextWithTooltipProps) {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);

  const { isOpen, x, y, refs, getReferenceProps, getFloatingProps } =
    useTooltip({
      position: side,
    });

  const checkIfOverflowing = useCallback(() => {
    const element = textRef.current;
    if (!element) return false;

    const range = document.createRange();
    range.selectNodeContents(element);
    const contentRect = range.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    return contentRect.width > elementRect.width;
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (checkIfOverflowing()) {
      setShouldShowTooltip(true);
    }
  }, [checkIfOverflowing]);

  const handleMouseLeave = useCallback(() => {
    setShouldShowTooltip(false);
  }, []);

  return (
    <>
      <span
        ref={useCallback(
          (node: HTMLSpanElement | null) => {
            textRef.current = node;
            refs.setReference(node);
          },
          [refs],
        )}
        {...getReferenceProps()}
        className={cn('block truncate', className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </span>

      {isOpen && shouldShowTooltip && (
        <div
          ref={refs.setFloating}
          {...getFloatingProps()}
          className="z-[9999] max-w-[600px] whitespace-pre-wrap break-words rounded-md bg-black px-3 py-2 text-xs font-medium leading-[18px] tracking-[0.016px] text-white shadow-md"
          style={{
            position: 'fixed',
            top: y ?? 0,
            left: x ?? 0,
          }}
        >
          {tooltipContent || children}
        </div>
      )}
    </>
  );
}
